import React, { useState, useEffect, useCallback, useRef } from 'react';
import Navbar from '../components/Navbar';
import {
  getDifySettings,
  updateDifySettings,
  getRedmineSettings,
  updateRedmineSettings,
  testRedmineConnection,
  triggerRedmineSync,
  getRedmineSyncJob,
} from '../services/api';
import { logout } from '../utils/auth';
import { useI18n } from '../i18n/I18nContext';
import styles from './AppSettings.module.css';

function AppSettings() {
  const { t } = useI18n();
  const [difyApiUrl, setDifyApiUrl] = useState('');
  const [chatKeySet, setChatKeySet] = useState(false);
  const [datasetKeySet, setDatasetKeySet] = useState(false);
  const [knowledgeBaseId, setKnowledgeBaseId] = useState('');
  const [chatApiKeyInput, setChatApiKeyInput] = useState('');
  const [datasetApiKeyInput, setDatasetApiKeyInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  /** 延遲解除 readOnly，降低 Chrome 將此表單當成登入頁而自動填入帳密 */
  const [chatKeyUnlocked, setChatKeyUnlocked] = useState(false);
  const [datasetKeyUnlocked, setDatasetKeyUnlocked] = useState(false);
  const [kbUnlocked, setKbUnlocked] = useState(false);

  const [redmineUrl, setRedmineUrl] = useState('');
  const [redmineKeySet, setRedmineKeySet] = useState(false);
  const [redmineConfigured, setRedmineConfigured] = useState(false);
  const [redmineApiKeyInput, setRedmineApiKeyInput] = useState('');
  const [redmineKeyUnlocked, setRedmineKeyUnlocked] = useState(false);
  const [savingRedmine, setSavingRedmine] = useState(false);
  const [testingRedmine, setTestingRedmine] = useState(false);

  // 同步狀態
  const [syncDryRun, setSyncDryRun] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncJob, setSyncJob] = useState(null);
  const pollTimerRef = useRef(null);

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const [{ data: dify }, { data: redmine }] = await Promise.all([
        getDifySettings(),
        getRedmineSettings(),
      ]);
      setDifyApiUrl(dify.dify_api_url || '');
      setChatKeySet(Boolean(dify.dify_chat_api_key_set));
      setDatasetKeySet(Boolean(dify.dify_dataset_api_key_set));
      setKnowledgeBaseId(dify.dify_knowledge_base_id || '');
      setChatApiKeyInput('');
      setDatasetApiKeyInput('');
      setChatKeyUnlocked(false);
      setDatasetKeyUnlocked(false);
      setKbUnlocked(false);

      setRedmineUrl(redmine.redmine_url || '');
      setRedmineKeySet(Boolean(redmine.redmine_api_key_set));
      setRedmineConfigured(Boolean(redmine.redmine_configured));
      setRedmineApiKeyInput('');
      setRedmineKeyUnlocked(false);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        return;
      }
      if (err.response?.status === 403) {
        setError(t('settings.error_admin_required'));
        return;
      }
      setError(err.response?.data?.error || t('settings.error_load_failed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const payload = {};
    if (chatApiKeyInput.trim()) {
      payload.dify_chat_api_key = chatApiKeyInput.trim();
    }
    if (datasetApiKeyInput.trim()) {
      payload.dify_dataset_api_key = datasetApiKeyInput.trim();
    }
    payload.dify_knowledge_base_id = knowledgeBaseId.trim();
    try {
      await updateDifySettings(payload);
      await load();
      window.alert(t('settings.alert_saved'));
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        return;
      }
      window.alert(err.response?.data?.error || t('settings.alert_save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleRedmineSubmit = async (e) => {
    e.preventDefault();
    setSavingRedmine(true);
    setError('');
    const payload = { redmine_url: redmineUrl.trim() };
    if (redmineApiKeyInput.trim()) {
      payload.redmine_api_key = redmineApiKeyInput.trim();
    }
    try {
      await updateRedmineSettings(payload);
      await load();
      window.alert(t('settings.alert_redmine_saved'));
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        return;
      }
      window.alert(err.response?.data?.error || t('settings.alert_save_failed'));
    } finally {
      setSavingRedmine(false);
    }
  };

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const pollJob = useCallback(async (jobId) => {
    try {
      const { data } = await getRedmineSyncJob(jobId);
      setSyncJob(data);
      if (data.status === 'completed' || data.status === 'failed' || data.status === 'interrupted') {
        stopPolling();
        setSyncing(false);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
      }
    }
  }, [stopPolling]);

  const handleRedmineSync = async () => {
    if (syncing) return;
    const modeLabel = syncDryRun ? t('settings.sync_mode_dry') : t('settings.sync_mode_prod');
    const confirmed = window.confirm(t('settings.sync_confirm', { modeLabel }));
    if (!confirmed) return;
    setSyncing(true);
    setSyncJob(null);
    stopPolling();
    try {
      const { data } = await triggerRedmineSync(syncDryRun);
      const jobId = data.job_id;
      pollJob(jobId);
      pollTimerRef.current = setInterval(() => pollJob(jobId), 5000);
    } catch (err) {
      setSyncing(false);
      if (err.response?.status === 401) {
        logout();
        return;
      }
      if (err.response?.status === 409) {
        window.alert(t('settings.alert_sync_running'));
        return;
      }
      window.alert(err.response?.data?.error || t('settings.alert_sync_start_failed'));
    }
  };

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const handleRedmineTestConnection = async () => {
    setTestingRedmine(true);
    try {
      const { data } = await testRedmineConnection();
      window.alert(data.message || t('settings.alert_test_ok'));
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        return;
      }
      window.alert(err.response?.data?.error || t('settings.alert_test_failed'));
    } finally {
      setTestingRedmine(false);
    }
  };

  return (
    <div className={styles.page}>
      <Navbar />
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>{t('settings.page_title')}</h1>
          <p className={styles.hint}>
            {t('settings.page_hint')}
          </p>
        </div>

        {error && <div className={styles.errorBox}>{error}</div>}

        {loading ? (
          <p>{t('settings.loading')}</p>
        ) : (
          <>
          <form onSubmit={handleSubmit} autoComplete="off">
            <div className={styles.formGroup}>
              <label>{t('settings.dify_url_readonly')}</label>
              <div className={styles.readOnlyBox}>{difyApiUrl || t('settings.dash')}</div>
            </div>

            <div className={styles.formGroup}>
              <label>
                {t('settings.dify_chat_key')}
                <span className={`${styles.badge} ${chatKeySet ? styles.badgeOk : styles.badgeOff}`}>
                  {chatKeySet ? t('settings.badge_set') : t('settings.badge_unset')}
                </span>
              </label>
              <input
                type="password"
                name="dify_chat_api_key"
                value={chatApiKeyInput}
                onChange={(e) => setChatApiKeyInput(e.target.value)}
                onFocus={() => setChatKeyUnlocked(true)}
                readOnly={!chatKeyUnlocked}
                placeholder={chatKeySet ? t('settings.placeholder_key_keep') : t('settings.placeholder_chat_key')}
                autoComplete="new-password"
              />
              <p className={styles.fieldHint}>
                {t('settings.hint_chat_key')}
              </p>
            </div>

            <div className={styles.formGroup}>
              <label>
                {t('settings.dify_dataset_key')}
                <span className={`${styles.badge} ${datasetKeySet ? styles.badgeOk : styles.badgeOff}`}>
                  {datasetKeySet ? t('settings.badge_set') : t('settings.badge_unset')}
                </span>
              </label>
              <input
                type="password"
                name="dify_dataset_api_key"
                value={datasetApiKeyInput}
                onChange={(e) => setDatasetApiKeyInput(e.target.value)}
                onFocus={() => setDatasetKeyUnlocked(true)}
                readOnly={!datasetKeyUnlocked}
                placeholder={datasetKeySet ? t('settings.placeholder_key_keep') : t('settings.placeholder_dataset_key')}
                autoComplete="new-password"
              />
              <p className={styles.fieldHint}>
                {t('settings.hint_dataset_key')}
              </p>
            </div>

            <div className={styles.formGroup}>
              <label>{t('settings.label_kb_id')}</label>
              <input
                type="text"
                name="dify_default_dataset_id"
                value={knowledgeBaseId}
                onChange={(e) => setKnowledgeBaseId(e.target.value)}
                onFocus={() => setKbUnlocked(true)}
                readOnly={!kbUnlocked}
                placeholder={t('settings.placeholder_kb_id')}
                autoComplete="off"
                spellCheck="false"
              />
              <p className={styles.fieldHint}>{t('settings.hint_kb_id')}</p>
            </div>

            <div className={styles.formActions}>
              <button type="submit" disabled={saving}>
                {saving ? t('settings.saving') : t('settings.save')}
              </button>
            </div>
          </form>

          <section className={styles.section} aria-labelledby="redmine-heading">
            <h2 id="redmine-heading" className={styles.sectionTitle}>
              {t('settings.section_redmine')}
            </h2>
            <p className={styles.sectionHint}>
              {t('settings.redmine_section_hint')}
            </p>
            <form onSubmit={handleRedmineSubmit} autoComplete="off">
              <div className={styles.formGroup}>
                <label htmlFor="redmine-url">{t('settings.label_redmine_url')}</label>
                <input
                  id="redmine-url"
                  type="url"
                  name="redmine_base_url"
                  value={redmineUrl}
                  onChange={(e) => setRedmineUrl(e.target.value)}
                  placeholder={t('settings.placeholder_redmine_url')}
                  autoComplete="off"
                  spellCheck="false"
                />
                <p className={styles.fieldHint}>{t('settings.hint_redmine_url')}</p>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="redmine-api-key">
                  {t('settings.label_redmine_key')}
                  <span
                    className={`${styles.badge} ${redmineKeySet ? styles.badgeOk : styles.badgeOff}`}
                  >
                    {redmineKeySet ? t('settings.badge_set') : t('settings.badge_unset')}
                  </span>
                </label>
                <input
                  id="redmine-api-key"
                  type="password"
                  name="redmine_rest_api_key"
                  value={redmineApiKeyInput}
                  onChange={(e) => setRedmineApiKeyInput(e.target.value)}
                  onFocus={() => setRedmineKeyUnlocked(true)}
                  readOnly={!redmineKeyUnlocked}
                  placeholder={redmineKeySet ? t('settings.placeholder_key_keep') : t('settings.placeholder_redmine_key')}
                  autoComplete="new-password"
                />
                <p className={styles.fieldHint}>
                  {t('settings.hint_redmine_key')}
                </p>
              </div>

              <div className={styles.formActions}>
                <button type="submit" disabled={savingRedmine}>
                  {savingRedmine ? t('settings.saving') : t('settings.save_redmine')}
                </button>
                <button
                  type="button"
                  className={styles.secondaryButton}
                  disabled={!redmineConfigured || testingRedmine}
                  onClick={handleRedmineTestConnection}
                >
                  {testingRedmine ? t('settings.testing') : t('settings.test_connection')}
                </button>
              </div>
            </form>

            {/* 同步區塊 */}
            <div className={styles.syncSection}>
              <h3 className={styles.syncTitle}>{t('settings.sync_title')}</h3>
              <div className={styles.syncControls}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={syncDryRun}
                    onChange={(e) => setSyncDryRun(e.target.checked)}
                    disabled={syncing}
                  />
                  <span>{t('settings.sync_dry_checkbox')}</span>
                </label>
                <button
                  type="button"
                  disabled={!redmineConfigured || syncing}
                  onClick={handleRedmineSync}
                >
                  {syncing ? t('settings.syncing') : t('settings.sync_start')}
                </button>
              </div>

              {syncJob && (
                <div className={styles.syncStatus}>
                  <div className={styles.syncStatusRow}>
                    <span>{t('settings.sync_status_label')}</span>
                    <strong>{syncJob.status}</strong>
                    {syncJob.dry_run && <span className={styles.dryRunBadge}>dry-run</span>}
                  </div>
                  {syncJob.progress && syncJob.status === 'running' && (
                    <div className={styles.syncProgress}>
                      <span>{t('settings.sync_projects_total')}{syncJob.progress.total_projects ?? t('settings.dash')}</span>
                      <span>{t('settings.sync_projects_done')}{syncJob.progress.synced_projects ?? 0}</span>
                      <span>{t('settings.sync_current_project')}{syncJob.progress.current_project || t('settings.dash')}</span>
                    </div>
                  )}
                  {syncJob.result && (
                    <div className={styles.syncResult}>
                      <span>{t('settings.sync_result_projects')}{syncJob.result.projects}</span>
                      <span>{t('settings.sync_result_wikis')}{syncJob.result.wikis}</span>
                      <span>{t('settings.sync_result_tickets')}{syncJob.result.tickets}</span>
                      {!syncJob.dry_run && <span>{t('settings.sync_result_dify')}{syncJob.result.dify_created}</span>}
                      {syncJob.result.error_count > 0 && (
                        <span className={styles.errorCount}>{t('settings.sync_error_count')}{syncJob.result.error_count}{t('settings.sync_error_count_suffix')}</span>
                      )}
                    </div>
                  )}
                  {syncJob.fatal_error && (
                    <div className={styles.fatalError}>{syncJob.fatal_error}</div>
                  )}
                  {syncJob.error_samples && syncJob.error_samples.length > 0 && (
                    <details className={styles.errorSamples}>
                      <summary>{t('settings.sync_error_details', { count: syncJob.error_samples.length })}</summary>
                      <ul>
                        {syncJob.error_samples.map((s, i) => (
                          <li key={i}>{s}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </div>
          </section>
          </>
        )}
      </div>
    </div>
  );
}

export default AppSettings;
